import React, { useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { VISITORS_MODE_BUTTONS } from '../../../base/config/constants';
import { getButtonNotifyMode, getButtonsWithNotifyClick, getToolbarButtons, isToolbarButtonEnabled } from '../../../base/config/functions.web';
import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n/functions';
import ContextMenu from '../../../base/ui/components/web/ContextMenu';
import { isReactionsButtonEnabled, isReactionsEnabled } from '../../../reactions/functions.web';
import { iAmVisitor } from '../../../visitors/functions';
import { setHangupMenuVisible, setOverflowMenuVisible, setToolbarHovered, showToolbox } from '../../actions.web';
import { NOT_APPLICABLE } from '../../constants';
import { getAllToolboxButtons, getJwtDisabledButtons, isToolboxVisible } from '../../functions.web';
import { useKeyboardShortcuts } from '../../hooks.web';
import { EndConferenceButton } from './EndConferenceButton';
import HangupMenuButton from './HangupMenuButton';
import { LeaveConferenceButton } from './LeaveConferenceButton';
import OverflowMenuButton from './OverflowMenuButton';
import Separator from './Separator';
import { groupBy } from "lodash";
import { BUTTONS_THRESHOLDS } from "../../../../11sight/features/toolbox/constants";
import AuditorNotesButton from "../../../../11sight/ui/components/toolbox/AuditorNotesButton";
import { isLocalParticipantModerator } from "../../../base/participants/functions";
import HangupButton from "../../../../11sight/ui/components/toolbox/HangupButton";
const useStyles = makeStyles()((theme) => {
    const buttonGroupBaseStyles = {
        display: "flex",
        gap: theme.spacing(2)
    };
    return {
        toolboxButtonsWrapper: {
            display: "grid !important",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "center",
            columnGap: theme.spacing(2),
            "&.only-center": {
                gridTemplateColumns: "initial"
            },
            "& > div": {
                whiteSpace: "nowrap"
            }
        },
        contextMenu: {
            position: "relative",
            right: "auto",
            margin: 0,
            marginBottom: "8px",
            maxHeight: "calc(100dvh - 100px)",
            minWidth: "240px"
        },
        hangupMenu: {
            position: "relative",
            right: "auto",
            display: "flex",
            flexDirection: "column",
            rowGap: "8px",
            margin: 0,
            padding: "16px",
            marginBottom: "4px"
        },
        buttonsGroupCenter: {
            ...buttonGroupBaseStyles,
            justifySelf: "center",
            gap: theme.spacing(3)
        },
        buttonsGroupStart: {
            ...buttonGroupBaseStyles,
            justifySelf: "start"
        },
        buttonsGroupEnd: {
            ...buttonGroupBaseStyles,
            justifySelf: "end"
        },
        buttonsGroupTopRight: {
            ...buttonGroupBaseStyles,
            position: "fixed",
            top: theme.spacing(2),
            right: theme.spacing(2)
        },
        buttonsGroupTopLeft: {
            ...buttonGroupBaseStyles,
            position: "fixed",
            top: theme.spacing(2),
            left: theme.spacing(2)
        },
        mobileContextWrapper: {
            background: isMobileBrowser() ? "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.64) 100%)!important" : "none"
        }
    };
});
const Toolbox = ({ _buttonsWithNotifyClick, _chatOpen, _clientWidth, _customToolbarButtons, _dialog, _disabled, _endConferenceSupported, _hangupMenuVisible, _isMobile, _isNarrowLayout, _jwtDisabledButtons, _overflowDrawer, _overflowMenuVisible, _reactionsButtonEnabled, _reactionsEnabled, _shiftUp, _toolbarButtons, _visible, dispatch, t, toolbarButtons, _isModerator, _addBackground }) => {
    const { classes, cx } = useStyles();
    const _toolboxRef = useRef(null);
    useKeyboardShortcuts(toolbarButtons);
    useEffect(() => {
        if (!_visible) {
            if (document.activeElement instanceof HTMLElement
                && _toolboxRef.current?.contains(document.activeElement)) {
                document.activeElement.blur();
            }
        }
    }, [_visible]);
    /**
     * Sets the visibility of the hangup menu.
     *
     * @param {boolean} visible - Whether or not the hangup menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, []);
    /**
     * Sets the visibility of the overflow menu.
     *
     * @param {boolean} visible - Whether or not the overflow menu should be
     * displayed.
     * @private
     * @returns {void}
     */
    const onSetOverflowVisible = useCallback((visible) => {
        dispatch(setOverflowMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, []);
    useEffect(() => {
        if (_hangupMenuVisible && !_visible) {
            onSetHangupVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [_hangupMenuVisible, _visible]);
    useEffect(() => {
        if (_overflowMenuVisible && _dialog) {
            onSetOverflowVisible(false);
            dispatch(setToolbarHovered(false));
        }
    }, [_overflowMenuVisible, _dialog]);
    /**
     * Key handler for overflow/hangup menus.
     *
     * @param {KeyboardEvent} e - Esc key click to close the popup.
     * @returns {void}
     */
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e?.stopPropagation();
            _hangupMenuVisible && dispatch(setHangupMenuVisible(false));
            _overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
        }
    }, [_hangupMenuVisible, _overflowMenuVisible]);
    /**
     * Sets the notify click mode for the buttons.
     *
     * @param {Object} buttons - The list of toolbar buttons.
     * @returns {void}
     */
    function setButtonsNotifyClickMode(buttons) {
        if (typeof APP === 'undefined' || !_buttonsWithNotifyClick?.length) {
            return;
        }
        Object.values(buttons).forEach((button) => {
            if (typeof button === 'object') {
                button.notifyMode = getButtonNotifyMode(button.key, _buttonsWithNotifyClick);
            }
        });
    }
    function getVisibleButtons() {
        if (isAuditor)
            return {
                mainMenuButtons: {},
                overflowMenuButtons: []
            };
        const buttons = getAllToolboxButtons(_customToolbarButtons);
        setButtonsNotifyClickMode(buttons);
        const isHangupVisible = isToolbarButtonEnabled("hangup", _toolbarButtons);
        const { order } = BUTTONS_THRESHOLDS.find(({ width }) => _clientWidth > width) || BUTTONS_THRESHOLDS[BUTTONS_THRESHOLDS.length - 1];
        const keys = Object.keys(buttons);
        const filtered = [
            ...order.map(key => buttons[key]),
            ...Object.values(buttons).filter((button, index) => !order.includes(keys[index]))
        ].filter(({ key, alias = NOT_APPLICABLE, mobileOnly = false, desktopOnly = false, moderatorOnly = false }) => ((desktopOnly && !_isMobile) || !desktopOnly) && ((mobileOnly && _isMobile) || !mobileOnly) && ((moderatorOnly && _isModerator) || !moderatorOnly) && !_jwtDisabledButtons.includes(key) && (isToolbarButtonEnabled(key, _toolbarButtons) || isToolbarButtonEnabled(alias, _toolbarButtons)));
        let sliceIndex = order.length + 2;
        if (isHangupVisible) {
            sliceIndex -= 1;
        }
        if (sliceIndex < filtered.length) {
            sliceIndex -= 1;
        }
        return {
            mainMenuButtons: groupBy(filtered.slice(0, sliceIndex), "alignment"),
            overflowMenuButtons: filtered.slice(sliceIndex)
        };
    }
    /**
     * Dispatches an action signaling the toolbar is not being hovered.
     *
     * @private
     * @returns {void}
     */
    function onMouseOut() {
        !_overflowMenuVisible && dispatch(setToolbarHovered(false));
    }
    /**
     * Dispatches an action signaling the toolbar is being hovered.
     *
     * @private
     * @returns {void}
     */
    function onMouseOver() {
        dispatch(setToolbarHovered(true));
    }
    /**
     * Toggle the toolbar visibility when tabbing into it.
     *
     * @returns {void}
     */
    const onTabIn = useCallback(() => {
        if (!_visible) {
            dispatch(showToolbox());
        }
    }, [_visible]);
    function renderToolboxContent() {
        const toolbarAccLabel = "toolbar.accessibilityLabel.moreActionsMenu";
        const containerClassName = `toolbox-content${_isMobile || _isNarrowLayout ? " toolbox-content-mobile" : ""}`;
        const { mainMenuButtons, overflowMenuButtons } = getVisibleButtons();
        const raiseHandInOverflowMenu = overflowMenuButtons.some(({ key }) => key === "raisehand");
        const showReactionsInOverflowMenu = (_reactionsEnabled && !_reactionsButtonEnabled && (raiseHandInOverflowMenu || _isNarrowLayout || _isMobile)) || overflowMenuButtons.some(({ key }) => key === "reactions");
        const showRaiseHandInReactionsMenu = showReactionsInOverflowMenu && raiseHandInOverflowMenu;
        const overflowButton = !isAuditor && Boolean(overflowMenuButtons.length) && (React.createElement(OverflowMenuButton, { ariaControls: "overflow-menu", buttons: overflowMenuButtons.reduce((acc, val) => {
                if (val.key === "reactions" && showReactionsInOverflowMenu) {
                    return acc;
                }
                if (val.key === "raisehand" && showRaiseHandInReactionsMenu) {
                    return acc;
                }
                if (acc.length) {
                    const prev = acc[acc.length - 1];
                    const group = prev[prev.length - 1].group;
                    if (group === val.group) {
                        prev.push(val);
                    }
                    else {
                        acc.push([val]);
                    }
                }
                else {
                    acc.push([val]);
                }
                return acc;
            }, []), isOpen: _overflowMenuVisible, key: "overflow-menu", onToolboxEscKey: onEscKey, onVisibilityChange: onSetOverflowVisible, showRaiseHandInReactionsMenu: showRaiseHandInReactionsMenu, showReactionsMenu: showReactionsInOverflowMenu }));
        return ((React.createElement("div", { className: containerClassName },
            React.createElement("div", { className: "toolbox-content-wrapper", onFocus: onTabIn, ...(_isMobile ? {} : {
                    onMouseOut,
                    onMouseOver
                }) },
                React.createElement("div", { className: cx(classes.toolboxButtonsWrapper, "toolbox-content-items", !mainMenuButtons.start && !mainMenuButtons.end && overflowMenuButtons.length === 0 && "only-center"), ref: _toolboxRef },
                    isAuditor && React.createElement(AuditorNotesButton, null),
                    React.createElement("div", { className: cx(classes.buttonsGroupTopLeft) },
                        _isMobile && overflowButton,
                        mainMenuButtons.topLeft?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupTopRight) }, mainMenuButtons.topRight?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupStart, "button-group-start") }, mainMenuButtons.start?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key }))),
                    React.createElement("div", { className: cx(classes.buttonsGroupCenter, "button-group-center painted-icons") },
                        mainMenuButtons.center?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key })),
                        isToolbarButtonEnabled("hangup", _toolbarButtons) && (_endConferenceSupported ? (React.createElement(HangupMenuButton, { ariaControls: "hangup-menu", isOpen: _hangupMenuVisible, key: "hangup-menu", notifyMode: getButtonNotifyMode("hangup-menu", _buttonsWithNotifyClick), onVisibilityChange: onSetHangupVisible, colorInClass: true },
                            React.createElement(ContextMenu, { accessibilityLabel: t(toolbarAccLabel), className: classes.hangupMenu, hidden: false, inDrawer: _overflowDrawer, onKeyDown: onEscKey },
                                React.createElement(EndConferenceButton, { buttonKey: "end-meeting", notifyMode: getButtonNotifyMode("end-meeting", _buttonsWithNotifyClick) }),
                                React.createElement(LeaveConferenceButton, { buttonKey: "hangup", notifyMode: getButtonNotifyMode("hangup", _buttonsWithNotifyClick) })))) : (React.createElement(HangupButton, { buttonKey: "hangup", customClass: "hangup-button", key: "hangup-button", notifyMode: getButtonNotifyMode("hangup", _buttonsWithNotifyClick), visible: isToolbarButtonEnabled("hangup", _toolbarButtons) })))),
                    React.createElement("div", { className: cx(classes.buttonsGroupEnd, "button-group-end") },
                        mainMenuButtons.end?.map(({ Content, key, ...rest }) => Content !== Separator && React.createElement(Content, { ...rest, buttonKey: key, key: key })),
                        !_isMobile && overflowButton))))));
    }
    if (_disabled) {
        return null;
    }
    const rootClassNames = `new-toolbox ${_visible ? "visible" : ""} ${_toolbarButtons.length ? "" : "no-buttons"} ${_chatOpen ? "shift-right" : ""} ${_addBackground !== false ? classes.mobileContextWrapper : ""}`;
    const isAuditor = window.APP.isMeetingAuditor;
    return ((React.createElement("div", { className: cx(rootClassNames, _shiftUp && "shift-up"), id: "new-toolbox" }, renderToolboxContent())));
};
function _mapStateToProps(state, ownProps) {
    const { conference } = state["features/base/conference"];
    const { isNarrowLayout } = state["features/base/responsive-ui"];
    const endConferenceSupported = conference?.isEndConferenceSupported() && isLocalParticipantModerator(state);
    const { customToolbarButtons, iAmRecorder, iAmSipGateway } = state["features/base/config"];
    const { hangupMenuVisible, overflowMenuVisible, overflowDrawer } = state["features/toolbox"];
    const { clientWidth } = state["features/base/responsive-ui"];
    let toolbarButtons = ownProps.toolbarButtons || getToolbarButtons(state);
    const _reactionsEnabled = isReactionsEnabled(state);
    if (iAmVisitor(state)) {
        toolbarButtons = VISITORS_MODE_BUTTONS.filter(e => toolbarButtons.indexOf(e) > -1);
    }
    let addBackground = ownProps.addBackground;
    return {
        _buttonsWithNotifyClick: getButtonsWithNotifyClick(state),
        _chatOpen: state["features/chat"].isOpen,
        _clientWidth: clientWidth,
        _customToolbarButtons: customToolbarButtons,
        _dialog: Boolean(state["features/base/dialog"].component),
        _disabled: Boolean(iAmRecorder || iAmSipGateway),
        _endConferenceSupported: Boolean(endConferenceSupported),
        _isMobile: isMobileBrowser(),
        _jwtDisabledButtons: getJwtDisabledButtons(state),
        _hangupMenuVisible: hangupMenuVisible,
        _isNarrowLayout: isNarrowLayout,
        _overflowMenuVisible: overflowMenuVisible,
        _overflowDrawer: overflowDrawer,
        _reactionsButtonEnabled: isReactionsButtonEnabled(state),
        _reactionsEnabled,
        _shiftUp: state["features/toolbox"].shiftUp,
        _toolbarButtons: toolbarButtons,
        _visible: isToolboxVisible(state),
        _isModerator: isLocalParticipantModerator(state),
        _addBackground: ownProps.addBackground !== undefined ? ownProps.addBackground : true
    };
}
export default translate(connect(_mapStateToProps)(Toolbox));
